import { firstQueryValue } from '@/shared/lib';
import type { SortOrder } from '@/shared/lib';
import type { RejectedMatrixSortField } from '../api/matrices.api';
import { parseCivilDateQuery, parsePositiveIntegerQuery } from './models-list-query';

const REJECTED_SORT_FIELDS: readonly RejectedMatrixSortField[] = ['rejectedAt', 'title'];

export const MAX_REJECTED_FILTER_INTEGER = 2147483647;

export interface RejectedListSort {
  sort: RejectedMatrixSortField | '';
  order: SortOrder | '';
}

export interface RejectedListFilters {
  rejectedAt?: string;
  createdBy?: number;
  revision?: number;
}

const isRejectedSortField = (value: unknown): value is RejectedMatrixSortField =>
  REJECTED_SORT_FIELDS.includes(value as RejectedMatrixSortField);

const isSortOrder = (value: unknown): value is SortOrder => value === 'asc' || value === 'desc';

export const toRejectedListSort = (sort: unknown, order: unknown): RejectedListSort =>
  isRejectedSortField(sort) && isSortOrder(order) ? { sort, order } : { sort: '', order: '' };

export const parseRejectedListSort = (query: Record<string, unknown>): RejectedListSort =>
  toRejectedListSort(firstQueryValue(query.sort), firstQueryValue(query.order));

export const parseBoundedIntegerQuery = (raw: unknown): number | undefined => {
  const value = parsePositiveIntegerQuery(raw);
  return value !== undefined && value <= MAX_REJECTED_FILTER_INTEGER ? value : undefined;
};

export const parseRejectedListFilters = (query: Record<string, unknown>): RejectedListFilters => ({
  rejectedAt: parseCivilDateQuery(query.rejectedAt),
  createdBy: parseBoundedIntegerQuery(query.createdBy),
  revision: parseBoundedIntegerQuery(query.revision),
});

export const countActiveRejectedFilters = (filters: RejectedListFilters): number =>
  [filters.rejectedAt, filters.createdBy, filters.revision].filter((value) => value !== undefined)
    .length;

export const buildRejectedListUrlQuery = (
  { sort, order }: RejectedListSort,
  filters: RejectedListFilters
): Record<string, string | number> => ({
  ...(sort && order ? { sort, order } : {}),
  ...(filters.rejectedAt ? { rejectedAt: filters.rejectedAt } : {}),
  ...(filters.createdBy ? { createdBy: filters.createdBy } : {}),
  ...(filters.revision ? { revision: filters.revision } : {}),
});
